import { doc, runTransaction, serverTimestamp } from 'firebase/firestore'
import { db } from '@/lib/firebase'
import { getCountryCode } from '@/lib/countryFlags'

/** Firestore collection for fan upvotes on “release this city next” cards. */
export const CITY_UPVOTES_COLLECTION = 'cityUpvotes'

/** Opaque-but-stable doc id from city/country grouping (valid Firestore document id characters). */
export function cityUpvoteFirestoreDocId(cityKey: string): string {
  const bytes = new TextEncoder().encode(cityKey)
  let binary = ''
  for (let i = 0; i < bytes.length; i++) binary += String.fromCharCode(bytes[i]!)
  return btoa(binary).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/u, '')
}

/** `upvotes` on each rollup doc is a list of Firebase Auth UIDs who upvoted this city fan vote. */
export function normalizeFanUpvotes(value: unknown): string[] {
  if (!Array.isArray(value)) return []
  return value.filter((v): v is string => typeof v === 'string' && v.length > 0)
}

export async function toggleCityUpvoteFirestore(params: {
  cityKey: string
  city: string
  country: string
  userId: string
}): Promise<'added' | 'removed'> {
  const docId = cityUpvoteFirestoreDocId(params.cityKey)
  const parentRef = doc(db, CITY_UPVOTES_COLLECTION, docId)
  const countryIso2 = params.country !== 'Unknown' ? getCountryCode(params.country) : null

  return runTransaction(db, async (transaction) => {
    const parentSnap = await transaction.get(parentRef)
    const existingIds = normalizeFanUpvotes(parentSnap.data()?.upvotes)
    const hadVote = existingIds.includes(params.userId)

    if (hadVote) {
      const next = existingIds.filter((id) => id !== params.userId)
      if (!parentSnap.exists()) {
        return 'removed'
      }
      transaction.update(parentRef, {
        upvotes: next,
        updatedAt: serverTimestamp(),
      })
      return 'removed'
    }

    const nextIds = [...existingIds]
    nextIds.push(params.userId)

    if (!parentSnap.exists()) {
      transaction.set(parentRef, {
        cityKey: params.cityKey,
        city: params.city,
        country: params.country,
        countryIso2: countryIso2 ?? null,
        region: null,
        upvotes: [params.userId],
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      })
      return 'added'
    }

    transaction.update(parentRef, {
      upvotes: nextIds,
      updatedAt: serverTimestamp(),
    })
    return 'added'
  })
}
